import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Star, Loader2, ThumbsUp } from "lucide-react";
import { toast } from "sonner";

/**
 * Banner de pesquisa NPS exibido no Portal Cliente após ticket concluído.
 * Não obrigatório. Cliente pode dispensar (válido por 7 dias).
 */
export default function PesquisaBanner() {
  const [pendentes, setPendentes] = useState([]);
  const [perguntas, setPerguntas] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [respostas, setRespostas] = useState({});
  const [salvando, setSalvando] = useState(false);

  const carregar = async () => {
    try {
      const { data } = await api.get("/pesquisa/disponivel");
      setPendentes(data.items || []);
      setPerguntas(data.perguntas || []);
    } catch (e) { /* silencioso */ }
  };

  useEffect(() => { carregar(); }, []);

  if (pendentes.length === 0) return null;

  const pesq = pendentes[0];
  const respondidasOk = perguntas.every((p) => respostas[p.id] !== undefined);

  const dispensar = async () => {
    try {
      await api.post(`/pesquisa/dispensar/${pesq.ticket_id}`);
      toast.success("Pesquisa adiada. Você poderá responder mais tarde.");
      carregar();
    } catch (e) { /* */ }
  };

  const enviar = async () => {
    if (!respondidasOk) {
      toast.error("Responda todas as perguntas antes de enviar"); return;
    }
    setSalvando(true);
    try {
      await api.post("/pesquisa/respostas", {
        ticket_id: pesq.ticket_id,
        respostas: Object.entries(respostas).map(([pergunta_id, nota]) => ({
          pergunta_id, nota: parseInt(nota, 10),
        })),
      });
      toast.success("Obrigado pelo seu feedback! 💙");
      setModalOpen(false);
      setRespostas({});
      carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar");
    } finally { setSalvando(false); }
  };

  return (
    <>
      <Card
        className="p-4 border-l-4 bg-gradient-to-r from-orange-50 to-amber-50"
        style={{ borderLeftColor: "#ff6b23" }}
        data-testid="pesquisa-banner"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-orange-500 text-white rounded-full p-2">
              <ThumbsUp size={20} />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-sm">
                Seu atendimento foi concluído! Como foi a experiência?
              </h3>
              <p className="text-xs text-slate-600 mt-0.5">
                {perguntas.length} pergunta{perguntas.length > 1 ? "s" : ""} · escala 0 a 10 · leva menos de 1 minuto
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm" variant="outline"
              onClick={dispensar}
              data-testid="pesquisa-dispensar"
            >
              Agora não
            </Button>
            <Button
              size="sm" className="bg-orange-600 hover:bg-orange-700"
              onClick={() => setModalOpen(true)}
              data-testid="pesquisa-responder"
            >
              <Star size={14} className="mr-1" /> Responder
            </Button>
          </div>
        </div>
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl" data-testid="modal-pesquisa">
          <DialogHeader>
            <DialogTitle>Pesquisa de Satisfação</DialogTitle>
            <DialogDescription>
              Sua opinião é fundamental! Avalie de 0 (péssimo) a 10 (excelente).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 max-h-96 overflow-y-auto pr-2">
            {perguntas.map((p, idx) => (
              <div key={p.id} className="space-y-2" data-testid={`pesq-pergunta-${p.id}`}>
                <p className="text-sm font-medium text-slate-800">
                  {idx + 1}. {p.texto}
                </p>
                <div className="flex gap-1 flex-wrap">
                  {[0,1,2,3,4,5,6,7,8,9,10].map((n) => {
                    const selecionado = respostas[p.id] === n;
                    const corBg = n <= 6 ? "red" : n <= 8 ? "amber" : "emerald";
                    return (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRespostas({ ...respostas, [p.id]: n })}
                        data-testid={`pesq-nota-${p.id}-${n}`}
                        className={`w-10 h-10 rounded-md border-2 text-sm font-bold transition ${
                          selecionado
                            ? `bg-${corBg}-600 text-white border-${corBg}-700`
                            : `bg-white text-slate-700 border-slate-300 hover:border-${corBg}-400 hover:bg-${corBg}-50`
                        }`}
                      >
                        {n}
                      </button>
                    );
                  })}
                </div>
                <div className="flex justify-between text-[10px] text-slate-500">
                  <span>Péssimo</span>
                  <span>Excelente</span>
                </div>
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)} disabled={salvando}>
              Cancelar
            </Button>
            <Button
              onClick={enviar}
              disabled={salvando || !respondidasOk}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="pesq-enviar"
            >
              {salvando && <Loader2 size={12} className="animate-spin mr-2" />}
              Enviar respostas
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
